/**
 * The sidebar navigation registry.
 *
 * ===========================================================================
 * WHY THE ICONS ARE OURS AND NOT FIGMA'S — INHERITED DESIGN DEFECT V4
 * ===========================================================================
 * The shared `Shell/Sidebar` component (node 12:908) draws every nav icon as an
 * EMPTY 16×16 rounded square — a 1.5px stroke in the text/muted token at radius 5,
 * with no glyph inside at all. The P2-T0 gate confirmed the
 * defect and directed a work-around: map each nav LABEL to a sensible lucide
 * icon and record the mapping. `iconName` below IS that mapping, and it is
 * returned in the phase report so a designer can accept or replace it per row
 * rather than re-deriving it from the code.
 *
 * ===========================================================================
 * TWO GATES, NOT ONE — `perm` AND `available`
 * ===========================================================================
 * `perm` is the permission gate. Appendix A of spec v1.3 is a CLOSED set of 34
 * keys, so every entry below either cites a real key or admits it has none. The
 * brief's requirement — "a user without `inquiries.view` never sees the entry" —
 * is enforced by `visibleNavSections()`, which drops an item whose keys the token
 * does not carry IN THE ACTIVE COUNTRY. Country-specific matters: a Country Manager
 * scoped to CD only must not see a nav entry that is live for them in IN.
 *
 * `available` is the "does this route exist yet" gate. Figma's sidebar lists 13
 * destinations across four sections; Phase 2 builds two of them. Rendering the
 * other eleven as live links would put a coordinator one click away from a blank
 * screen, and rendering them greyed-out would invent a disabled state the design
 * does not have. They are therefore declared here — complete with icon and
 * permission mapping, so Phases 3–5 inherit the decisions instead of re-making
 * them — and simply not rendered until their phase flips `available` to true.
 *
 * THE DL FILES KEY QUESTION IS ANSWERED — CLOSED IN v7, NOT DROPPED. This entry
 * carried `perm: null` + `available: false` through Phases 2-4 because Appendix A
 * had no key that plausibly governed a downloadable-files surface, and inventing
 * `files.view` would have put a key in a token no role, seed or matrix test knew
 * about. The screen now exists and its route is gated on `crf.view`, so the row
 * is gated on `crf.view` too — the answer came from a controller, not from a new
 * key. Dashboard is now the ONLY deliberate `perm: null`, and the registry test
 * pins that count so a third null cannot appear unnoticed.
 */

import type { PermissionKey } from '../lib/permissions';

export interface NavItem {
  readonly id: string;
  /** The label, verbatim from Figma's `NavItem/*` nodes. */
  readonly label: string;
  /**
   * lucide-react export name. OUR mapping, not Figma's — see the header.
   * Kept as a string so this registry stays a plain data module; `Sidebar.tsx`
   * owns the string→component table and typechecks it.
   */
  readonly iconName: NavIconName;
  /** Route, relative to `/countries/:cc`. */
  readonly path: string;
  /**
   * Permission keys, ANY of which reveals the entry. `null` = no Appendix A key
   * governs this surface (see the header — this is a recorded gap, not a bypass:
   * `null` combined with `available: false` means it never renders).
   */
  readonly perm: readonly PermissionKey[] | null;
  /**
   * Phase 2.5b: the entry leads to a GLOBAL surface, so its `perm` gate is
   * evaluated against permissions held in ANY accessible country
   * (`NavAuthority.permsAnywhere`) rather than the active one.
   *
   * This mirrors the API exactly, not a preference: `/users` routes carry no
   * `:cc` and their `@RequirePerm('users.manage')` is evaluated as "held in at
   * least one country" (`rbac.controller.ts`). A CD-only `users.manage` holder
   * standing in IN can open the users list and the API will answer — hiding
   * the entry there would hide a real capability, which is the opposite of the
   * D5 rule that the UI hides what the caller canNOT do. Country-scoped admin
   * surfaces (agent keys, network rules — D3 objects of the active country)
   * keep the default active-country gate.
   */
  readonly global?: boolean;
  /** Does a route exist in this phase? */
  readonly available: boolean;
  /** Which phase builds it. Documentation for the next builder. */
  readonly phase: number;
}

export interface NavSection {
  /** Section header, verbatim from Figma's `SectionHeader` nodes (uppercase there). */
  readonly title: string;
  readonly items: readonly NavItem[];
}

export type NavIconName =
  /*
   * `Tags` was orphaned when Catalogue pricing merged into Products and is worn
   * again by the Licences row — see that entry for why a serial is a tag and why
   * the obvious alternative (`KeyRound`) is refused.
   */
  | 'Tags'
  | 'Package'
  | 'LayoutDashboard'
  | 'Inbox'
  | 'FileText'
  | 'Columns3'
  | 'Download'
  | 'FlaskConical'
  | 'Rocket'
  | 'Server'
  | 'FileCheck'
  | 'ClipboardList'
  | 'CircleCheck'
  | 'Receipt'
  | 'Building2'
  // Phase 2.5b — the AUTHORED Admin section (no Figma exists for it).
  | 'Users'
  | 'KeyRound'
  | 'ShieldCheck'
  | 'ListChecks'
  // Phase 3a · U5 — the AUTHORED Sales section (no Figma sidebar row exists).
  | 'FileSignature'
  | 'PackageCheck'
  // Phase 5 · U5.5 — the AUTHORED Renewals section (no Figma sidebar row exists).
  | 'CalendarClock'
  // Phase 5 · U2/5.6 — the customer reminder draft queue.
  | 'Mails'
  // Tax groups (design U5, migration 30) — a percentage is what the screen manages.
  | 'Percent'
  /*
   * Country settings (India go-live, A3). Deliberately NOT `Building2`, which `parties`
   * already wears: that glyph is an office block and it means "a customer company" on
   * every screen it appears on. This row is the REGISTERED ENTITY — the name and address
   * that print as the seller's identity on a document — so it takes the civic building
   * rather than the commercial one. Two destinations wearing one glyph is the defect the
   * `UserCog` note above records.
   */
  | 'Landmark'
  /*
   * Roles (custom-roles design §1.6). Deliberately NOT `ShieldCheck`, which Network
   * rules already wears, and not `Users`, which is the Users entry: a role is neither
   * a person nor a firewall. `UserCog` is a person being CONFIGURED, which is what a
   * role template is — and reusing either neighbour's glyph would make three different
   * destinations look like one.
   */
  | 'UserCog'
  // v17 FIELD TRACKING (F4) — the AUTHORED Field section (no Figma exists for it).
  // Attendance is a day checked off; a visit is a place somebody went.
  | 'CalendarCheck'
  | 'MapPin'
  /*
   * Public holidays. Deliberately NOT `CalendarCheck` — which Attendance already
   * wears TWO ROWS ABOVE IT in the same drawn group — and not `CalendarClock`,
   * which is Renewals. Three calendars in one sidebar, two of them adjacent, is
   * exactly the "three destinations look like one" defect the `UserCog` note
   * records. `CalendarOff` carries the difference in the glyph AND in the
   * meaning: this screen defines the days STRUCK OUT of the working calendar,
   * where Attendance is the day checked off.
   */
  | 'CalendarOff'
  // v22-D — the field map's sweep; MapPin is Visits, CalendarCheck is
  // Attendance, and a third meaning needs a third glyph.
  | 'Radar'
  /*
   * Field settings (Admin). `LocateFixed` — a location crosshair — because what the
   * screen governs is location CAPTURE (toggles, radius, retention), and every
   * settings-flavoured glyph nearby is taken: `Landmark` is Country settings,
   * `ListChecks` the checklist, `ShieldCheck` Network rules. A generic gear would
   * make the one screen about personal-data capture look like any other knob.
   */
  | 'LocateFixed'
  /* ── v19 DASHBOARDS · W2 — `Globe`: the GROUP dashboard, every active country in one
   * view. NOT `LayoutDashboard` (the country Dashboard row): `admin-infra-nav.test.ts` and
   * `field-nav.test.ts` pin that no two destinations wear one glyph, and a consolidated
   * view of the whole group is exactly what a globe is. ── */
  | 'Globe'
  /* ── v19 DASHBOARDS · W1 (Team section) — BEGIN. Two glyphs for the two Team rows.
   * `Gauge` is LOAD (how much is on each developer's plate); `Activity` is WHAT HAPPENED
   * (throughput over a period). `Users` is Admin › Users (the people as records) and is
   * deliberately not re-worn; `UserCog` is Roles. ── */
  | 'Gauge'
  | 'Activity'
  /* ── v19 DASHBOARDS · W1 — END ── */
  /*
   * VOCABULARY ADMIN (migrations 38/39) — three glyphs for three rows.
   * `MonitorSmartphone` is PLATFORMS (what a record ships to — screens, literally);
   * `ToggleLeft` is PLATFORM AVAILABILITY (a per-country switch is what the screen
   * IS); `Layers` is STACKS (a tech stack drawn as itself). `Package` is Products,
   * `Tags` is Licences, `Server` is Deployed Projects — all deliberately not
   * re-worn: two destinations wearing one glyph is the recorded defect.
   */
  | 'MonitorSmartphone'
  | 'ToggleLeft'
  | 'Layers'
  /*
   * v21 · UNIT C — the acceptance chase (the AUTHORED Acceptance section). A
   * document being LOOKED FOR: every row is a signed record whose customer
   * acceptance is not on file. Deliberately NOT `FileCheck`, which Sign Offs
   * wears — the chase is the queue's opposite (the queue waits for a signature
   * that has not happened; the chase hunts a document a signature promised),
   * and one glyph on both would make the two registers look like one
   * destination. `FileText` is CRFs, `FileSignature` is Quotes.
   */
  | 'FileSearch'
  /*
   * v24 Phase B — the Payment exceptions register. A worklist of CRFs whose
   * opening balance must be entered by hand; an alert glyph, because every row
   * is something waiting on a person. Deliberately NOT `Receipt` (spent on the
   * sales side) — a shared glyph would make two registers read as one.
   */
  | 'CircleAlert';

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    title: 'Pipeline',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        iconName: 'LayoutDashboard',
        path: '',
        // No Appendix A key governs the COUNTRY dashboard. `dashboard.group` is the
        // grp-only GROUP dashboard (a different surface, Phase 5), so gating this on
        // it would hide the landing page from every ordinary user. Country scope is
        // the gate, and the route guard already enforces that.
        perm: null,
        available: true,
        phase: 2,
      },
      {
        id: 'inquiries',
        label: 'Inquiries',
        iconName: 'Inbox',
        path: 'inquiries',
        perm: ['inquiries.view'],
        available: true,
        phase: 2,
      },
      {
        id: 'crfs',
        label: 'CRFs',
        iconName: 'FileText',
        path: 'crfs',
        /*
         * AVAILABLE SINCE v7 BLOCK A — the six page-03 screens are mounted
         * (`features/crfs/routes.tsx`). The permission stays `crf.view`,
         * matching GET /countries/:cc/crfs's own guard — the Work Board lesson:
         * a menu that hides a screen the API would serve is the nav lying.
         */
        perm: ['crf.view'],
        available: true,
        phase: 3,
      },
    ],
  },
  {
    title: 'Development',
    items: [
      {
        /*
         * AVAILABLE SINCE U5 (Phase 4). The board and the work-order record are
         * both mounted (`features/work-pipeline/routes.tsx`); this flag stayed
         * `false` after slice 1 shipped, which left the only entry point to the
         * whole pipeline being a typed URL.
         *
         * THE PERMISSION IS `crf.view`, MATCHING THE ROUTE'S OWN GATE — and the
         * old pair was not a harmless over-narrowing. `GET /countries/:cc/work-board`
         * and `GET /work-orders/:id` are both `@RequirePerm('crf.view')`, while
         * this menu demanded `work.assign` OR `work.update`. Against the seeded
         * matrix that hid the pipeline from two roles who may read it — and one of
         * them owns a surface inside it: ACCOUNTS holds `crf.view` and
         * `billing.record`, and the work-order record was, when this was written,
         * the one screen that records payment (owner ruling: payment status has its
         * own surface, never folded into a CRF edit). Tech Support (`crf.view`) was
         * hidden likewise.
         *
         * ⚠ AMENDED (Train E, fork 15): the payment card is no longer exclusive to
         * the work-order record — the CRF record (`features/crfs/CrfRecordPage.tsx`)
         * mounts the same `PaymentPanel` as its own separate card, still under
         * `billing.record`, so Accounts can record payment on a CRF that has no
         * work order yet. The argument above stands (a hidden pipeline still hid a
         * screen Accounts owns); only the word "only" fell. `crfs/record-comment-
         * honesty.test.ts` reddens if the exclusive wording returns while both
         * mounts stand.
         *
         * A menu that hides a screen the API would serve is the nav lying while the
         * code is right — rule 8, and the same failure the Appendix A guard exists
         * to catch. Nothing is widened here: the API remains the authority, and
         * every action inside the screen is still gated on its own key.
         */
        id: 'work-board',
        label: 'Work Board',
        iconName: 'Columns3',
        path: 'work',
        perm: ['crf.view'],
        available: true,
        phase: 4,
      },
      {
        id: 'dl-files',
        label: 'DL Files',
        iconName: 'Download',
        path: 'dl-files',
        /*
         * THE RECORDED GAP IS CLOSED — by a screen existing, not by a new key.
         *
         * This read `perm: null` with "no Appendix A key exists for this surface"
         * for as long as the surface did not exist. It does now (v7 Block B), and
         * the route that serves it — `GET /countries/:cc/dl-files` — is gated on
         * `crf.view` by its controller. So the key that governs this row is the
         * key the API already enforces. Nothing was added to the closed 34-key
         * set; a question that had no answer while the screen was hypothetical
         * simply has one now that it is real.
         */
        perm: ['crf.view'],
        available: true,
        phase: 4,
      },
      {
        id: 'testing',
        label: 'Testing',
        iconName: 'FlaskConical',
        path: 'testing',
        /*
         * `crf.view`, NOT `testing.record` — the Work Board correction, again.
         *
         * This reserved the WRITE keys because no screen existed to say otherwise.
         * The screen is a cross-CRF READ of test rounds, and its controller gates
         * on `crf.view`. Gating the row on `testing.record`/`testing.return` would
         * hide a register the API serves from a Coordinator whose entire job is
         * chasing failed rounds — the nav lying while the code is right (rule 8),
         * which is exactly the failure Work Board was corrected for.
         *
         * Nothing is widened: recording a round is still gated on its own key
         * inside the work-order screen, which is the only place a round is written.
         */
        perm: ['crf.view'],
        available: true,
        phase: 4,
      },
    ],
  },
  {
    title: 'Deployment',
    items: [
      {
        id: 'deployment',
        label: 'Deployment',
        iconName: 'Rocket',
        path: 'deployment',
        /*
         * `crf.view`, NOT `deploy.record` — the same correction as Work Board,
         * Testing and DL Files. This register READS every deployment in a country;
         * its controller gates on `crf.view`. Recording a deployment is still
         * gated on `deploy.record`, inside the work-order screen that does it.
         */
        perm: ['crf.view'],
        available: true,
        phase: 5,
      },
      {
        id: 'deployed-projects',
        /*
         * ⚠ THE LABEL MOVED AND THE `id` DID NOT (design D-8, Projects C8). The register
         * contains `work_type='project'` records now — its read never had a predicate that
         * could exclude one — so a sidebar row reading "Deployed Projects" would name the
         * narrower of the word's two meanings while linking to the wider one. `id`,
         * `path` and the row type stay put: they are identifiers, and renaming an
         * identifier to fix a reader's ambiguity costs a migration of every reference and
         * buys nothing the copy change has not already bought.
         */
        label: 'Deployed Work',
        iconName: 'Server',
        path: 'deployed',
        /*
         * THE "NO SEPARATE READ KEY" NOTE IS RESOLVED, and the resolution is not
         * the one it guessed. It read: "Appendix A grades only `deploy.record` —
         * there is no separate READ key for deployments. Using the write key as
         * the read gate is the closest honest reading; flagged so Phase 5 can
         * confirm or split it." Phase 5 is here, and the answer needed no split:
         * `crf.view` already governs reading a country's CRF data, and this screen
         * is one row per deployed CRF. Nothing was added to the closed 34-key set.
         */
        perm: ['crf.view'],
        available: true,
        phase: 5,
      },
      {
        id: 'sign-offs',
        label: 'Sign Offs',
        iconName: 'FileCheck',
        path: 'signoffs',
        /*
         * CHASING IS NOT SIGNING — the sharpest instance of the whole pattern.
         * Gating this on `signoff.*` would hide the outstanding-signature queue
         * from the Coordinator whose entire job is chasing it, while leaving it
         * visible only to the people who are the bottleneck. The screen records
         * nothing; it is a read of which signature each deployed CRF is waiting on.
         */
        perm: ['crf.view'],
        available: true,
        phase: 5,
      },
    ],
  },
  {
    title: 'Reports',
    items: [
      {
        id: 'project-received',
        label: 'Project Received',
        iconName: 'ClipboardList',
        path: 'reports/received',
        // v19 D4 (W2): the route is mounted in `features/reports/routes.tsx`; the row and
        // the screen land in ONE change. `reports.view` is what the route enforces.
        perm: ['reports.view'],
        available: true,
        phase: 6,
      },
      {
        id: 'complete-work',
        label: 'Complete Work',
        iconName: 'CircleCheck',
        path: 'reports/complete',
        // v19 D4 (W2): mounted in `features/reports/routes.tsx`; `reports.view` is the route's gate.
        perm: ['reports.view'],
        available: true,
        phase: 6,
      },
      {
        id: 'billing',
        label: 'Billing',
        iconName: 'Receipt',
        path: 'billing',
        // v19 D4 (W2): mounted in `features/reports/routes.tsx`; `billing.view` is the route's gate
        // (`reports.controller.ts`). The honest subset — no invoice entity exists.
        perm: ['billing.view'],
        available: true,
        phase: 6,
      },
      {
        id: 'parties',
        label: 'Parties',
        iconName: 'Building2',
        path: 'parties',
        // v19 D4 (W2): the Parties REPORT (projects + lifetime value per party) is the screen
        // this reservation waited for — mounted in `features/reports/routes.tsx` on
        // `parties.view`, the route's own gate. Phase 1 built the parties API; the list
        // screen was never in Phase 2's scope (the brief's Part B lists Login, Picker,
        // Shell, Dashboard, Inquiries).
        perm: ['parties.view'],
        available: true,
        phase: 6,
      },
    ],
  },
];

/** What the caller may act with, in ONE country. */
export interface NavAuthority {
  /** `grp:true` — every permission in every active country (spec §10). */
  readonly isGroupAdmin: boolean;
  /** Permission keys held in the ACTIVE country. */
  readonly perms: readonly PermissionKey[];
  /**
   * Phase 2.5b: permission keys held in ANY accessible country — the union the
   * session already computes per country, flattened. Only `global: true` items
   * read it (see that field's doc for why). OPTIONAL so existing constructors
   * keep compiling; when absent, global items fall back to the ACTIVE-country
   * gate — the stricter reading, never the looser one.
   */
  readonly permsAnywhere?: readonly PermissionKey[];
}

function grants(item: NavItem, authority: NavAuthority): boolean {
  if (item.perm === null) {
    // No key governs it. Reachability is decided by `available` alone — which for
    // every `perm: null` entry except the dashboard is `false`.
    return true;
  }
  if (authority.isGroupAdmin) return true;
  // Global surfaces are entered on "held in at least one country" — the API's
  // own evaluation of `@RequirePerm` on an unscoped route (rbac.controller.ts).
  const held =
    item.global === true ? (authority.permsAnywhere ?? authority.perms) : authority.perms;
  return item.perm.some((key) => held.includes(key));
}

/**
 * The sections to render: available items the caller is permitted to see, with
 * empty sections dropped.
 *
 * A section header with nothing under it is worse than no section — it tells a
 * Support user that "DEPLOYMENT" exists and is empty for them, which reads as a
 * bug rather than as a permission boundary.
 */
export function visibleNavSections(authority: NavAuthority): NavSection[] {
  const sections: NavSection[] = [];
  for (const section of NAV_SECTIONS) {
    const items = section.items.filter((item) => item.available && grants(item, authority));
    if (items.length > 0) sections.push({ title: section.title, items });
  }
  return sections;
}

/** Absolute path for a nav item in the active country. */
export function navHref(countryCode: string, item: NavItem): string {
  return item.path === ''
    ? `/countries/${countryCode}`
    : `/countries/${countryCode}/${item.path}`;
}

/* =============================================================================
 * PHASE 2.5b — THE ADMIN SECTION IS A SEPARATE REGISTRY, AND THAT IS DELIBERATE
 * =============================================================================
 * `NAV_SECTIONS` above is pinned by `nav.test.ts` as "the thirteen destinations
 * the FIGMA sidebar lists" — it is the record of a design document, and the
 * admin surface is not in that document (the 2.5b brief's own acceptance calls
 * every admin screen AUTHORED). Folding these items into `NAV_SECTIONS` would
 * either break that record or force the test to stop meaning what it says. A
 * second registry keeps both statements true: thirteen from Figma, and an
 * authored Admin section that says it is authored.
 *
 * PERMISSION GATING — a recorded deviation from one brief sentence. The brief
 * writes "'Admin' nav section, visible only to grp / users.manage holders",
 * but the three infrastructure screens sit on APIs guarded by `config.manage`
 * (`agent-keys.controller.ts`, `network-rules.controller.ts`,
 * `admin.controller.ts` — all `@RequirePerm('config.manage')`). Gating their
 * nav entries on `users.manage` would show a users-admin three links that 403,
 * and hide from a Country Manager the screens the API happily serves them. Each
 * item therefore carries the permission ITS API enforces, and the section is
 * visible to whoever can use at least one item — which includes every
 * grp / users.manage holder the brief names once the users slice adds its
 * entry. Recorded in PHASE25b-REPORT.
 *
 * SEAM FOR THE USERS/ROLES STREAM: append your item(s) here — e.g.
 * `{ id: 'users', label: 'Users', iconName: …, path: 'admin/users',
 *    perm: ['users.manage'], available: true, phase: 2.5 }` — and the section,
 * `visibleNavSections`-style gating and the sidebar all pick it up unchanged.
 * ========================================================================== */
export const ADMIN_NAV_SECTION: NavSection = {
  title: 'Admin',
  items: [
    {
      /*
       * ROLES — custom-roles design §1.6, owner ruling 2026-08-11.
       *
       * ⚠ IT SITS ABOVE `Users` DELIBERATELY, AND THE ARRAY ORDER IS THE RENDER ORDER —
       * no sort is applied anywhere between this registry and `Sidebar.tsx`. That is
       * worth stating because it was WRONG: this docblock and `RolesListPage`'s header
       * both claimed role management came first while the array put `users` above it,
       * and a review caught the source and the report disagreeing. The entry moved
       * rather than the sentence, because the sentence is the design's: the owner's
       * words were "first make an option to create a role, and then create the user".
       * Role management did not exist, so the only way to give somebody access was to
       * pick one of seven names nothing in the product explained; reading the Admin
       * section top-to-bottom is now the order the work actually happens in.
       * `admin-infra-nav.test.ts` measures the position so it cannot drift back.
       *
       * `users.manage`, matching the READ gate on `GET /roles`: everybody who assigns a
       * role needs to see what it does. Authoring is stricter (group-admin only, OD-C)
       * and is enforced on the write routes and by the screen, not by this entry —
       * gating the nav on grp would hide the catalogue from the very administrators the
       * catalogue exists for.
       *
       * `global: true`, for the same reason `users` carries it: roles have no
       * `country_id` and the routes behind the screen carry no `:cc`, so the entry must
       * not vanish when the shell switches country.
       */
      id: 'roles',
      label: 'Roles',
      iconName: 'UserCog',
      path: 'admin/roles',
      perm: ['users.manage'],
      global: true,
      available: true,
      phase: 5,
    },
    {
      /**
       * Users are GLOBAL objects (D5: the `users` table has no `country_id`),
       * so this is the one Admin entry with `global: true`: a `users.manage`
       * holder sees it from EVERY country they can stand in, because the API
       * answers `GET /users` for them regardless of which country the shell is
       * showing (`@RequirePerm` on an unscoped route = "held in at least one
       * country"). The infra entries below stay on the active-country gate —
       * their objects belong to the country in the URL.
       */
      id: 'users',
      label: 'Users',
      iconName: 'Users',
      path: 'admin/users',
      perm: ['users.manage'],
      global: true,
      available: true,
      phase: 2.5,
    },
    {
      id: 'agent-keys',
      label: 'Agent keys',
      iconName: 'KeyRound',
      path: 'admin/agent-keys',
      perm: ['config.manage'],
      available: true,
      phase: 2.5,
    },
    {
      id: 'network-rules',
      label: 'Network rules',
      iconName: 'ShieldCheck',
      path: 'admin/network-rules',
      perm: ['config.manage'],
      available: true,
      phase: 2.5,
    },
    {
      /*
       * ⚠ THE `catalogue` ENTRY ("Catalogue pricing", `admin/catalogue`) STOOD ABOVE
       * THIS ONE AND IS RETIRED (roles/products design §2.1, owner ruling 2026-08-11):
       * the Product master and Catalogue pricing screens merged into the ONE product
       * screen this entry now names. Removed WITH its route and screen in the same
       * change — a nav entry pointing at a dead route is the five-times defect — and
       * `products-page.test.tsx` pins the id's absence, exactly as `go-live`'s is.
       *
       * The label was "Product master" and follows the merge: the screen now carries
       * names, shelves AND prices, and a label naming half of that would lie. Every
       * pointer at the old screen name follows in the same change (the NAME_UNVERIFIED
       * refusal's "Admin → Product master" sentence and its pins).
       *
       * `config.manage` in the ACTIVE country, unchanged — the gate every route on the
       * merged screen enforces (`catalog.controller.ts`), per-country since migration
       * 22. `global: true` stays dropped: the object belongs to the country in the URL,
       * and the group admin's cross-country view is the SCREEN's affordance (its
       * country pill), not a nav-gate union.
       */
      id: 'products',
      label: 'Products',
      iconName: 'Package',
      path: 'admin/products',
      perm: ['config.manage'],
      available: true,
      phase: 5,
    },
    {
      /*
       * COUNTRY SETTINGS — India go-live, cluster A3, owner ruling 4 (*"then the
       * letterhead… then GSTIN"*).
       *
       * THE ENTRY THAT MAKES THE CHECKLIST ACTIONABLE. MEASURED on `crf_v3_ta`: all six
       * countries hold a placeholder `countries.address_block`, so `POST /quotes/:id/pdf`
       * refuses every quotation in every country — and the column's only writer,
       * `PATCH /countries/:cc`, had no user interface anywhere in the product. The
       * checklist could name the defect and nobody in the business could clear it.
       *
       * POSITION: immediately ABOVE `go-live`, and the two are read together — the
       * checklist says what is outstanding, this row is where the letterhead half of it is
       * fixed. `admin-infra-nav.test.ts` pins the ordering, the gate and that the route is
       * mounted.
       *
       * `config.manage` in the ACTIVE country, matching what `PATCH /countries/:cc`
       * actually enforces (D3, own country only). NOT `global: true`: the row being edited
       * IS the country in the URL, so an entry that survived a country switch would point
       * at somebody else's letterhead.
       */
      id: 'country-settings',
      label: 'Country settings',
      iconName: 'Landmark',
      path: 'admin/country-settings',
      perm: ['config.manage'],
      available: true,
      phase: 5,
    },
    {
      /*
       * PUBLIC HOLIDAYS — the working calendar behind the attendance register.
       *
       * ⚠ TWO KEYS, AND `perm` IS AN OR (`item.perm.some(...)`), WHICH IS THE
       * POINT. The screen holds two panels under two different permissions: the
       * country's calendar is `config.manage` (what `holidays.controller.ts`
       * enforces) and the calendar-gap list is `users.manage` (what
       * `UsersController` enforces, because a person's calendar is a property of
       * the PERSON). A single-key row would either hide the gap list from a
       * users-admin or show a Country Manager a row whose other half 403s — the
       * same deviation this section's header already records for the infra
       * three. Each panel re-checks its own key; this row only decides whether
       * there is anything at all to open.
       */
      id: 'holidays',
      label: 'Public holidays',
      iconName: 'CalendarOff',
      path: 'admin/holidays',
      perm: ['config.manage', 'users.manage'],
      available: true,
      phase: 5,
    },
    {
      /*
       * GO-LIVE CHECKLIST — RETIRED 2026-08-10 (tax-groups design §6, G2) AND RESTORED
       * 2026-08-14 ON THE OWNER'S REVERSAL: *"I removed it and that was wrong."*
       *
       * The retirement's argument was that four of the five queues it listed had stopped
       * being refusals, which was true and is still true — the restored screen lists ONE
       * queue as the gate (product NAMES, the flag `NAME_UNVERIFIED` still refuses over)
       * and shows the price flag separately, labelled as informational. What the argument
       * missed is what replaced the screen: `/health` answers with a bare integer, so
       * production could say "one product is unconfirmed" and NOTHING in the product
       * could say which one. That is the gap this entry closes.
       *
       * `config.manage` in the ACTIVE country, like the other infra entries and like the
       * products screen the checklist sends people to. NOT `global: true`: the screen
       * reads the catalogue through the country in the URL for a Country Admin, and a
       * group admin's cross-country view is the SCREEN's behaviour, not a nav-gate union.
       *
       * Position kept from before the retirement — after `products`, whose row is where
       * every item on this list is cleared. `admin-infra-nav.test.ts` pins presence,
       * gate and position; that file's absence pin is superseded there WITH the callout.
       */
      id: 'go-live',
      label: 'Go-live checklist',
      iconName: 'ListChecks',
      path: 'admin/go-live',
      perm: ['config.manage'],
      available: true,
      phase: 5,
    },
    {
      /*
       * TAX GROUPS (design U5, migration 30, owner ruling OD-1) — the named tax
       * configurations a country's quotes price under, replacing the retired
       * tax-rules surface. `config.manage` because that is what every route on
       * `/countries/:cc/tax-groups` except the picker LIST enforces — the same
       * per-country gate as the pricing and infra entries, for the same D3 reason:
       * `tax_groups` carries a `country_id`, so a Country Manager maintains their
       * OWN country's tax configuration.
       */
      id: 'tax-groups',
      label: 'Tax groups',
      iconName: 'Percent',
      path: 'admin/tax-groups',
      perm: ['config.manage'],
      available: true,
      phase: 5,
    },
    {
      /*
       * PLATFORMS (migration 38, owner rulings Q8/Q8-b) — the GROUP's shared platform
       * vocabulary: author, rename, re-scope (`applies_to`), retire.
       *
       * `config.global`, WHICH IS EXACTLY WHAT EVERY ROUTE BEHIND THE SCREEN ENFORCES
       * INCLUDING THE READ (`platforms.controller.ts:74-107`, all
       * `@RequirePerm('config.global')` + `assertGlobalConfigAccess` in the service):
       * this is the maintenance view and it shows RETIRED values, so the list is not
       * `config.manage` and not open. Gating the row on anything looser would show a
       * Country Manager a link that 403s on arrival — the Work Board defect.
       *
       * `global: true`: `platforms` has no `country_id`, the API routes carry no
       * `:cc`, and `config.global` is evaluated as "held anywhere"
       * (`hasPermissionAnywhere`, `access.ts:136`) — the row must not vanish when the
       * shell switches country.
       */
      id: 'platforms',
      label: 'Platforms',
      iconName: 'MonitorSmartphone',
      path: 'admin/platforms',
      perm: ['config.global'],
      global: true,
      available: true,
      phase: 6,
    },
    {
      /*
       * PLATFORM AVAILABILITY (owner decision D-2) — ONE country's answer over the
       * group's platform list: which values THIS country offers in its pickers.
       *
       * `config.manage` in the ACTIVE country, matching both routes behind the screen
       * (`platforms.controller.ts:127-147`: GET/PUT `/countries/:cc/platforms`, both
       * `@RequirePerm('config.manage')` + `@CountryScoped()`). NOT `config.global` —
       * holding the group key is not holding the country one, in both directions
       * (`platforms.e2e-spec.ts` suite (b)). NOT `global: true`: the rows being
       * edited ARE the country in the URL; an entry that survived a country switch
       * would point at another country's shop window.
       *
       * Directly under `platforms` so the group half and the country half of one
       * vocabulary read together — the country-settings/go-live pairing precedent.
       */
      id: 'platform-availability',
      label: 'Platform availability',
      iconName: 'ToggleLeft',
      path: 'admin/platform-availability',
      perm: ['config.manage'],
      available: true,
      phase: 6,
    },
    {
      /*
       * STACKS (migration 39, owner decision D-2b) — what our engineers build
       * projects in. `config.global` and `global: true` for exactly the `platforms`
       * row's reasons (`stacks.controller.ts:68-96`, every route including the read).
       *
       * There is NO availability twin for stacks and none may be added: a country
       * does not choose what our engineers build in — `/countries/:cc/stacks` does
       * not exist (D-2b, absence pinned by `stacks.e2e-spec.ts` suite (a)).
       */
      id: 'stacks',
      label: 'Stacks',
      iconName: 'Layers',
      path: 'admin/stacks',
      perm: ['config.global'],
      global: true,
      available: true,
      phase: 6,
    },
    {
      /*
       * FIELD SETTINGS (v17 field tracking, F4) — the per-country switchboard:
       * the three feature toggles, the arrival-agreement radius, the live-session
       * ceiling, and the two RETENTION windows for position history.
       *
       * `config.manage` in the ACTIVE country, because that is exactly what both
       * routes behind the screen enforce (`field-location.controller.ts`:
       * GET/PATCH `/countries/:cc/field/config`, both
       * `@RequirePerm('config.manage')` + `@CountryScoped()`). NOT any `field.*`
       * key: those gate the capture and the registers, and none of them is a
       * configuration right. NOT `global: true`: the row being edited IS the
       * country in the URL — an entry that survived a country switch would point
       * at another country's retention promise, which is a legal setting, not a
       * preference. Appended after `tax-groups`; the infra ordering pins
       * (`admin-infra-nav.test.ts`) assert relative positions of THEIR ids only,
       * and `field-nav.test.ts` pins this row's own gate and mounting.
       */
      id: 'field-settings',
      label: 'Field settings',
      iconName: 'LocateFixed',
      path: 'admin/field-settings',
      perm: ['config.manage'],
      available: true,
      phase: 6,
    },
    /* ── v19 D3 · W2 — delimited block: the GROUP DASHBOARD row ─────────────────── */
    {
      /*
       * THE GROUP-CONSOLIDATED VIEW (v19 D3, the one hard rule) — `/countries/:cc/group`,
       * mounted in `features/dashboards/routes.tsx`.
       *
       * `dashboard.group`, WHICH IS EXACTLY WHAT THE ROUTE ENFORCES
       * (`group-dashboard.controller.ts`: `@RequirePerm('dashboard.group')`). Seeded
       * GRP_ONLY in `permissions.ts`, so in practice the row renders for a group admin
       * or a planted per-user override; no role template can carry it. NOT `config.global`
       * — that is the key that CLEARS the FX gate, not the key that READS the view, and
       * the two are different grants on purpose (proposal §8 #3).
       *
       * `global: true`: the API route carries no `:cc` and the view is every active
       * country by definition; the row must not vanish when the shell switches country.
       *
       * THE GLYPH IS `Globe` — every active country in one view — and NOT
       * `LayoutDashboard`, which is the country Dashboard row: `admin-infra-nav.test.ts`
       * and `field-nav.test.ts` pin that no two destinations wear one glyph (a shared
       * icon is two rows wearing one name), and the first cut of this row learned it the
       * measured way (both pins reddened). The union member and the `Sidebar.tsx` table
       * entry land with this row, one line each, in W2's delimited comments.
       */
      id: 'group-dashboard',
      label: 'Group dashboard',
      iconName: 'Globe',
      path: 'group',
      perm: ['dashboard.group'],
      global: true,
      available: true,
      phase: 6,
    },
    /* ── end v19 D3 · W2 block ─────────────────────────────────────────────────── */
  ],
};

/**
 * The Admin section as the caller may see it, or `null` when nothing in it is
 * theirs to see — same rule as `visibleNavSections`: a section header over an
 * empty list reads as a bug, not a boundary.
 */
export function visibleAdminNavSection(authority: NavAuthority): NavSection | null {
  const items = ADMIN_NAV_SECTION.items.filter(
    (item) => item.available && grants(item, authority),
  );
  return items.length > 0 ? { title: ADMIN_NAV_SECTION.title, items } : null;
}

/* =============================================================================
 * PHASE 3a · U5 — THE SALES SECTION IS A THIRD REGISTRY, FOR THE 2.5b REASON
 * =============================================================================
 * `NAV_SECTIONS` is pinned by `nav.test.ts` as "the thirteen destinations the
 * FIGMA sidebar lists". Quotes and Sales orders are not among them: the sidebar
 * frame (12:908) has no row for either, and `02 Inquiry Pipeline` draws no quote
 * screen at all. Folding them in would either break that record or force the test
 * to stop meaning what it says — the exact trade Phase 2.5b faced and resolved by
 * adding a second registry rather than editing the first.
 *
 * So this is the third: thirteen from Figma, an authored Admin section that says
 * it is authored, and an authored Sales section that says the same.
 *
 * PERMISSIONS ARE THE ONES THE ROUTES ENFORCE, not a convenient superset:
 *  · Quotes       → `quotes.view` (`GET /countries/:cc/quotes`,
 *                   `@RequirePerm('quotes.view')`). NOT `quotes.create`: a Sales
 *                   Manager who may confirm but not raise must still see the list.
 *  · Sales orders → `so.view` (`GET /countries/:cc/sales-orders`). Appendix A has
 *                   `so.view` and no create/push row, which is the whole shape of
 *                   Phase 3a: the orders are readable and nothing writes them
 *                   except a quote confirmation.
 *
 * Both are COUNTRY-scoped (no `global: true`): a quote belongs to a country, and
 * the routes carry `:cc`.
 * ========================================================================== */
export const SALES_NAV_SECTION: NavSection = {
  title: 'Sales',
  items: [
    {
      id: 'quotes',
      label: 'Quotes',
      // A priced document somebody signs off on. `FileText` is taken by CRFs and
      // `Receipt` by Billing — a quote is neither, and reusing either glyph would
      // make two different destinations look like the same thing.
      iconName: 'FileSignature',
      path: 'quotes',
      perm: ['quotes.view'],
      available: true,
      phase: 3,
    },
    {
      id: 'sales-orders',
      label: 'Sales orders',
      // A committed order, checked off. Deliberately not a cart or a truck: what
      // this screen shows is a confirmed commitment, not a delivery.
      iconName: 'PackageCheck',
      path: 'sales-orders',
      perm: ['so.view'],
      available: true,
      phase: 3,
    },
  ],
};

/** The Sales section as the caller may see it, or `null`. Same rule as above. */
export function visibleSalesNavSection(authority: NavAuthority): NavSection | null {
  const items = SALES_NAV_SECTION.items.filter(
    (item) => item.available && grants(item, authority),
  );
  return items.length > 0 ? { title: SALES_NAV_SECTION.title, items } : null;
}


/* =============================================================================
 * PHASE 5 · U5.5 — RENEWALS IS A FOURTH REGISTRY, FOR THE SAME REASON AS THE OTHER TWO
 * =============================================================================
 * `NAV_SECTIONS` is pinned by `nav.test.ts` as "the thirteen destinations the FIGMA sidebar
 * lists". There is no Figma row for Renewals — the screen was specified in the U5.5 brief,
 * not drawn — so folding it in would either break that record or force the test to stop
 * meaning what it says. Phase 2.5b faced the trade and resolved it by adding a registry;
 * Phase 3a's Sales section did the same. This is the fourth, and it says so.
 *
 * ⚠ THE PERMISSION IS `quotes.view`, WHICH IS EXACTLY WHAT THE ROUTE ENFORCES.
 * `renewals.controller.ts` carries `@RequirePerm('quotes.view')`. This project has now met
 * the Work Board defect — a menu demanding a key the route does not — five times, in every
 * standalone register plus the Work Board itself, and `nav.test.ts` pins each of those. This
 * row is pinned the same way rather than trusted.
 *
 * The tempting narrower gate here would be `so.view`, on the reasoning that a renewal comes
 * off a sales order. It does NOT, since U5.4: a Tally Sales subscription confirms into no
 * sales order at all, and Tally Sales — the role that sells them — holds `quotes.view` and
 * no `so.view`. Gating this row on `so.view` would hide a Tally Sales seat's own renewals
 * from it, which is the defect exactly, for the sixth time.
 *
 * COUNTRY-scoped (no `global: true`): a contract belongs to a country and the route
 * carries `:cc`.
 * ========================================================================== */
export const RENEWALS_NAV_SECTION: NavSection = {
  title: 'Renewals',
  items: [
    {
      id: 'renewals',
      label: 'Renewals',
      // A calendar with a clock: this destination is about WHEN something ends. `Receipt`
      // is Billing and `PackageCheck` is Sales orders — a renewal is neither a payment nor
      // a delivery, and reusing either glyph would make two destinations look like one.
      iconName: 'CalendarClock',
      path: 'renewals',
      perm: ['quotes.view'],
      available: true,
      phase: 5,
    },
    {
      /*
       * ⚠ U2 / 5.6 — THE CUSTOMER REMINDER DRAFT QUEUE, AND THE GATE IS `quotes.view`
       * BECAUSE THAT IS WHAT THE ROUTE THIS ROW OPENS ENFORCES.
       *
       * `renewals.controller.ts`: `@Get('renewal-drafts') @RequirePerm('quotes.view')`.
       *
       * THE TEMPTING WRONG GATE HERE IS `quotes.send`, and it is tempting for a good
       * reason — sending is the point of the screen, and `POST renewal-drafts/:id/send`
       * really does carry `quotes.send`. It is still wrong, and the seeded matrix says
       * exactly why: ACCOUNTS holds `quotes.view` and NOT `quotes.send`. Gating this row
       * on the send key would hide from Accounts a queue the API happily serves them —
       * the Work Board defect for the SIXTH time, in a project that has already met it
       * five. Accounts chase money; what is about to be chased from a customer is their
       * business to read, and the D5 rule handles the rest one level down: the SEND
       * CONTROL is absent inside the screen, and the screen itself is not.
       *
       * The permission on the row is the READ permission, always. `nav.test.ts` pins it.
       *
       * COUNTRY-scoped (no `global: true`): a draft belongs to a country's ledger and the
       * route carries `:cc`.
       */
      id: 'renewal-drafts',
      label: 'Reminder drafts',
      // A STACK of letters — a queue of mail nobody has sent. `CalendarClock` above is the
      // register (WHEN something ends); this destination is a pile of drafted LETTERS
      // awaiting a person, and giving the two rows one glyph would make them look like a
      // single destination listed twice. (lucide has no `MailClock`; `Mails` is the stack.)
      iconName: 'Mails',
      path: 'renewal-drafts',
      perm: ['quotes.view'],
      available: true,
      phase: 5,
    },
    {
      /*
       * ⚠ THE LICENCE REGISTER — A ROW THIS REGISTRY NEVER RESERVED, AND SAYING SO
       * IS THE POINT OF THIS COMMENT.
       *
       * Every standalone register that shipped in v7 (`testing`, `dl-files`,
       * `deployment`, `deployed`, `signoffs`) already existed here as an
       * `available: false` reservation, and mounting it flipped a flag somebody had
       * thought about in Phase 3. This one is different: `NAV_SECTIONS` has held
       * `reports/received`, `reports/complete`, `billing` and `parties` as
       * unavailable reservations since Phase 3, and NOT ONE OF THEM IS A LICENCE
       * REGISTER. So this is a registry CHANGE rather than a registry flag, it is
       * named as a cost rather than discovered inside a cluster, and `nav.test.ts`
       * moves with it — with a docblock recording that the pin was EXTENDED, not
       * relaxed.
       *
       * WHY IT SITS UNDER `Renewals` RATHER THAN IN A FIFTH REGISTRY. The licence
       * estate is the SECOND renewal call list. The whole design of the due-soon
       * worklist is the rule that stops it and the renewals register naming the same
       * customer and product twice (the imported `live_contract` CTE, shared
       * verbatim rather than copied), and an operator who opens one should see the
       * other beside it. A fifth registry would also need a `visibleLicencesNavSection`
       * composed into `components/shell/Sidebar.tsx`, and this cluster deliberately
       * does not edit the shell. FLAGGED for whoever adds the second licence
       * destination — the dispute review report is already specified — because two
       * rows is the point at which its own section earns the Sidebar line.
       *
       * ⚠ `quotes.view`, WHICH IS EXACTLY WHAT THE ROUTE ENFORCES. All five handlers
       * on `tally-licences.controller.ts` carry `@RequirePerm('quotes.view')` and the
       * build adds NO permission key. The tempting wrong gates are `reports.view` —
       * reserved by two rows that do not exist, so borrowing it pre-empts a decision
       * nobody made — and `crf.view`, which gates the CRF registers, and a licence is
       * not a CRF. The Work Board defect, refused for the seventh time.
       *
       * THE GLYPH IS `Tags`, and it is chosen by elimination rather than taste. It is
       * already in the union and already mapped in `Sidebar.tsx`'s table, so the row
       * needs no edit to a file this cluster does not own; it is an ORPHAN — nothing
       * rendered has worn it since Catalogue pricing merged into Products — so it
       * collides with no visible destination; and a serial IS a tag stuck to an
       * installation. It is deliberately NOT `KeyRound`: that is Agent keys, and a key
       * glyph on this row would advertise the one thing the register refuses to store.
       *
       * COUNTRY-scoped (no `global: true`): a licence belongs to a country's estate
       * and every route carries `:cc`.
       */
      id: 'licences',
      label: 'Licences',
      iconName: 'Tags',
      path: 'licences',
      perm: ['quotes.view'],
      available: true,
      phase: 5,
    },
  ],
};

/** The Renewals section as the caller may see it, or `null`. Same rule as the others. */
export function visibleRenewalsNavSection(authority: NavAuthority): NavSection | null {
  const items = RENEWALS_NAV_SECTION.items.filter(
    (item) => item.available && grants(item, authority),
  );
  return items.length > 0 ? { title: RENEWALS_NAV_SECTION.title, items } : null;
}

/* =============================================================================
 * v17 · FIELD TRACKING — A FIFTH REGISTRY, FOR THE REASON THE OTHER THREE EXIST
 * =============================================================================
 * `NAV_SECTIONS` is pinned by `nav.test.ts` as "the thirteen destinations the
 * FIGMA sidebar lists". There is no Figma row for field tracking — the surface
 * was specified by the v17 overnight brief, not drawn — so folding it in would
 * either break that record or force the pin to stop meaning what it says.
 * Admin, Sales and Renewals each resolved the same trade with a registry that
 * says it is authored; this is the fifth, and it says so.
 *
 * PERMISSIONS ARE THE ONES THE ROUTES ENFORCE — the Work Board rule, which this
 * project has now enforced seven times and pins every time:
 *  · Attendance → `field.attendance.view` (`GET /countries/:cc/field/attendance`,
 *    `attendance.controller.ts`). NOT `field.attendance.record`: recording is the
 *    ENGINEER's key and gates a phone action, not this register — an engineer
 *    without the view key reads their own day on the phone (`/me`), not here.
 *  · Visits → `field.visits.view` (`GET /countries/:cc/field/visits`). Same split.
 * NEITHER row names `field.location.view`: coordinates are masked PER ROW by the
 * server (Rule 14), and a register full of `'•••'` is still the register — hiding
 * it from a Coordinator because they cannot see coordinates would hide the
 * business record to protect a value the API already withholds.
 *
 * THE TIMELINE HAS NO ROW: `/field/timeline` needs an engineer and a day, and a
 * nav row cannot carry either. The registers' rows are the way in (each carries
 * both), which the route file also records — the `parties/new` precedent.
 *
 * Both COUNTRY-scoped (no `global: true`): every route carries `:cc` and a
 * clock cycle or a visit belongs to a country's working day.
 * ========================================================================== */
export const FIELD_NAV_SECTION: NavSection = {
  title: 'Field',
  items: [
    {
      id: 'field-attendance',
      label: 'Attendance',
      // A day checked off. `CalendarClock` is Renewals (WHEN something ends);
      // this is whether a day was WORKED — the check, not the clock.
      iconName: 'CalendarCheck',
      path: 'field/attendance',
      perm: ['field.attendance.view'],
      available: true,
      phase: 6,
    },
    {
      id: 'field-visits',
      label: 'Visits',
      // A place somebody went. `Building2` is Parties — the customer as a
      // RECORD; this destination is the going-there.
      iconName: 'MapPin',
      path: 'field/visits',
      perm: ['field.visits.view'],
      available: true,
      phase: 6,
    },
    {
      /*
       * v22-D — THE FIELD MAP. Gated on the RAW key, exactly as the API route
       * is: `permissions.ts` documents `field.location.view` as "RAW
       * coordinates + map + trails of OTHERS", and a nav row the register keys
       * could open would lead to a screen the server 403s — the defect the
       * Admin-section header records from the other direction.
       */
      id: 'field-map',
      label: 'Live map',
      // A sweep finding positions — not `MapPin` (Visits: one place) and not
      // `CalendarCheck` (Attendance: a day). The map is the SWEEP.
      iconName: 'Radar',
      path: 'field/map',
      perm: ['field.location.view'],
      available: true,
      phase: 6,
    },
  ],
};

/** The Field section as the caller may see it, or `null`. Same rule as the others. */
export function visibleFieldNavSection(authority: NavAuthority): NavSection | null {
  const items = FIELD_NAV_SECTION.items.filter(
    (item) => item.available && grants(item, authority),
  );
  return items.length > 0 ? { title: FIELD_NAV_SECTION.title, items } : null;
}

/* =============================================================================
 * v19 DASHBOARDS · W1 — THE TEAM SECTION IS A SIXTH REGISTRY (BEGIN OF W1 BLOCK)
 * =============================================================================
 * `NAV_SECTIONS` is pinned by `nav.test.ts` as "the thirteen destinations the
 * FIGMA sidebar lists". Figma 07 draws a Team Dashboard (12:131) but no sidebar
 * row for it, and the who-does-what view was specified by the v19 brief, not
 * drawn — so, like Admin, Sales, Renewals and Field before it, this section says
 * it is authored rather than bending the Figma record.
 *
 * PERMISSIONS ARE THE ONES THE ROUTES ENFORCE (the Work Board rule, pinned by
 * `team-nav.test.ts`):
 *  · Team → `crf.view` (`GET /team/workload`, `team.controller.ts`). The brief asks
 *    for the Developer to be ON this screen, masked, beside the Dev Head who reads
 *    the money — so the gate is the board's own read key, not `work.assign`.
 *    Proposal §6 (b) records the one-decorator change if the owner wants it hidden.
 *  · Who does what → `reports.view` (`GET /throughput`). A management report;
 *    Developer does not hold it and does not see the row.
 *
 * BOTH ARE `global: true`: the routes are BARE (no `:cc`) and evaluated "held in
 * at least one country", so a CD-only `crf.view` holder standing in IN can open the
 * Team view and the API answers with a CD-only page. Hiding the row there would hide
 * a real capability — the opposite of the D5 rule.
 * ========================================================================== */
export const TEAM_NAV_SECTION: NavSection = {
  title: 'Team',
  items: [
    {
      id: 'team-workload',
      label: 'Team',
      // LOAD — how much is on each developer's plate, and what is stuck.
      iconName: 'Gauge',
      path: 'team',
      perm: ['crf.view'],
      global: true,
      available: true,
      phase: 7,
    },
    {
      id: 'team-throughput',
      label: 'Who does what',
      // WHAT HAPPENED over a period — a record, not a ranking.
      iconName: 'Activity',
      path: 'team/throughput',
      perm: ['reports.view'],
      global: true,
      available: true,
      phase: 7,
    },
  ],
};

/** The Team section as the caller may see it, or `null`. Same rule as the others. */
export function visibleTeamNavSection(authority: NavAuthority): NavSection | null {
  const items = TEAM_NAV_SECTION.items.filter(
    (item) => item.available && grants(item, authority),
  );
  return items.length > 0 ? { title: TEAM_NAV_SECTION.title, items } : null;
}
/* ── v19 DASHBOARDS · W1 — END OF W1 BLOCK ── */

/* =============================================================================
 * v21 · UNIT C — THE ACCEPTANCE SECTION IS A SEVENTH REGISTRY, FOR THE REASON THE OTHER FIVE EXIST
 * =============================================================================
 * `NAV_SECTIONS` is pinned by `nav.test.ts` as "the thirteen destinations the
 * FIGMA sidebar lists". The acceptance chase is not among them — Train F's fork
 * 21 specified the route and no Figma frame draws a row for it — so folding it
 * into the Deployment section beside Sign Offs, where it semantically belongs,
 * would either break that record or force the pin to stop meaning what it says.
 * Admin, Sales, Renewals, Field and Team each resolved the same trade with a
 * registry that says it is authored; this is the seventh, and it says so.
 *
 * ⚠ ONE ROW, AND THE LICENCES PRECEDENT IS ANSWERED, NOT IGNORED. The Licences
 * row's own comment rules "two rows is the point at which its own section earns
 * the Sidebar line" — and Licences had a semantically honest authored home
 * (Renewals: the licence estate is the second renewal call list). This
 * destination has none: it is deployment-pipeline paperwork, and the section it
 * belongs beside is the pinned Figma record. A one-row authored section is the
 * smaller cost than mislabelling the pin; if a second acceptance surface ever
 * ships (fork 21's own labelling writer, say), it lands here.
 *
 * ⚠ `crf.view`, WHICH IS EXACTLY WHAT THE ROUTE ENFORCES.
 * `registers.controller.ts:147-149` — `@Get('signoffs/acceptance-chase')`,
 * `@RequirePerm('crf.view')`, `@CountryScoped()`. The tempting wrong gate is
 * `signoff.project` — the screen is ABOUT project sign-offs — and the
 * controller's own header says why
 * that is the Work Board defect again: chasing a document is reading, and the
 * Coordinator whose job is chasing holds no `signoff.*`. Gating the row on the
 * signing key would show the chase list only to the people being chased.
 *
 * COUNTRY-scoped (no `global: true`): the route carries `:cc` and a sign-off
 * row belongs to a country (`sp.country_id` is the scope predicate).
 * ========================================================================== */
export const ACCEPTANCE_NAV_SECTION: NavSection = {
  title: 'Acceptance',
  items: [
    {
      id: 'acceptance-chase',
      label: 'Acceptance chase',
      // A document being looked for — see the union's own note for the glyphs
      // this deliberately is not.
      iconName: 'FileSearch',
      path: 'signoffs/acceptance-chase',
      perm: ['crf.view'],
      available: true,
      phase: 8,
    },
  ],
};

/** The Acceptance section as the caller may see it, or `null`. Same rule as the others. */
/**
 * v24 PHASE B — THE PAYMENTS SECTION (task B2.2). An AUTHORED registry, like
 * Acceptance: `NAV_SECTIONS` is pinned as the thirteen destinations the Figma
 * sidebar lists, and this one is not among them.
 *
 * ONE KEY ON THE ROW, TWO ON THE ROUTE — and why that is not a mismatch.
 * `grants()` is OR across `perm`, so listing both of the API's keys here would
 * SHOW the row to a seat holding `amounts.view` alone, who then meets the
 * server's 403. `payments.record` is the destination's identity: the register
 * exists to be ACTED on, and every seat that can act (Accounts, Country Manager)
 * holds `amounts.view` with it — AM-2's guardrail warns on exactly the shape
 * where it would not. The page itself refuses by the same key.
 */
export const PAYMENTS_NAV_SECTION: NavSection = {
  title: 'Payments',
  items: [
    {
      id: 'payment-exceptions',
      label: 'Payment exceptions',
      iconName: 'CircleAlert',
      path: 'payments/exceptions',
      perm: ['payments.record'],
      available: true,
      phase: 9,
    },
  ],
};

export function visiblePaymentsNavSection(authority: NavAuthority): NavSection | null {
  const items = PAYMENTS_NAV_SECTION.items.filter(
    (item) => item.available && grants(item, authority),
  );
  return items.length > 0 ? { title: PAYMENTS_NAV_SECTION.title, items } : null;
}

export function visibleAcceptanceNavSection(authority: NavAuthority): NavSection | null {
  const items = ACCEPTANCE_NAV_SECTION.items.filter(
    (item) => item.available && grants(item, authority),
  );
  return items.length > 0 ? { title: ACCEPTANCE_NAV_SECTION.title, items } : null;
}

/* ===========================================================================
 * THE DISPLAY GROUPING — presentation only, and deliberately NOT the registry
 * ===========================================================================
 *
 * MEASURED 2026-08-27 on trunk 32c0234: a group admin sees 36 rows under TEN
 * section headers, Admin alone holds 13 of them, and `Acceptance` is a header
 * over a SINGLE row. Ten headings for thirty-six rows is more chrome than the
 * content justifies, and the owner asked for it divided properly.
 *
 * ⚠ WHY THIS IS A SEPARATE LAYER RATHER THAN A REGROUPED REGISTRY.
 * `nav.test.ts` pins `NAV_SECTIONS` as "all thirteen destinations the Figma
 * sidebar lists", in order, and states the reason: the registry is how later
 * phases inherit the icon and permission decisions instead of re-deriving
 * them, so losing a row loses the decision. Two further cases assert the later
 * registries are NOT folded into it. Rewriting section membership would
 * destroy that record to buy a presentation change.
 *
 * So the record is left EXACTLY as it stands — every section, every order,
 * every permission gate — and the sidebar is told separately how to PRESENT
 * it. The registry answers "what exists and who may see it"; this answers
 * "how is it stacked". They can now be changed independently, which is the
 * whole point: a future Figma revision moves this constant and touches no gate.
 *
 * ⚠ IT FAILS OPEN, BY CONSTRUCTION. An item this constant forgets is NOT
 * dropped — {@link groupNavForDisplay} returns it under its own registry
 * heading. A silently unreachable destination is the one outcome worse than an
 * ugly sidebar, so the failure mode is a stray heading, never a lost row.
 * `nav-display.test.ts` asserts the leftovers list is EMPTY for the real
 * registry, so the fail-open path is a safety net and not the normal case.
 */
export interface NavDisplayGroup {
  /**
   * The heading, or `null` for the leading group that carries no heading at
   * all. Dashboard is one row and a header over one row is the defect this
   * change exists to remove — the same reason `Acceptance` is folded away
   * below. Linear, Asana and Airwallex all open with unheaded rows above the
   * first section for exactly this.
   */
  readonly title: string | null;
  readonly itemIds: readonly string[];
}

export const NAV_DISPLAY_GROUPS: readonly NavDisplayGroup[] = [
  { title: null, itemIds: ['dashboard'] },
  /*
   * SELL / DELIVER / RENEW follow the shape of the work rather than the shape
   * of the build. `Acceptance chase` joins DELIVER beside `sign-offs`, which
   * its own registry comment already calls "the chase's sibling register" —
   * it was only a separate section because the pinned thirteen could not be
   * touched, which is precisely the constraint this layer lifts.
   */
  // v24 Phase B: payment exceptions sit with Sell — Accounts' follow-on to what
  // was sold. A DISPLAY placement only; the registry section title is the record.
  { title: 'Sell', itemIds: ['inquiries', 'quotes', 'sales-orders', 'payment-exceptions'] },
  {
    title: 'Deliver',
    itemIds: [
      'crfs',
      'work-board',
      'dl-files',
      'testing',
      'deployment',
      'deployed-projects',
      'sign-offs',
      'acceptance-chase',
    ],
  },
  { title: 'Renew', itemIds: ['renewals', 'renewal-drafts', 'licences'] },
  /*
   * `holidays` IS AN ADMIN-REGISTRY ITEM DRAWN UNDER FIELD, on the same
   * principle that moved `group-dashboard` out of Admin: group by what a
   * screen IS, not by which key opens it. Its whole purpose is to make the
   * attendance register answerable, and that register is two rows above it.
   */
  { title: 'Field', itemIds: ['field-attendance', 'field-visits', 'field-map', 'holidays'] },
  /*
   * INSIGHT is reading, not configuring — which is why `group-dashboard` moves
   * here out of Admin. It is a DASHBOARD; it sat under Admin only because
   * `dashboard.group` is a grp-only key and Admin was where grp-only rows had
   * landed. Grouping by what a screen IS beats grouping by which key opens it.
   */
  {
    title: 'Insight',
    itemIds: [
      'project-received',
      'complete-work',
      'billing',
      'parties',
      'team-workload',
      'team-throughput',
      'group-dashboard',
    ],
  },
  {
    title: 'Admin',
    itemIds: [
      'roles',
      'users',
      'agent-keys',
      'network-rules',
      'products',
      'country-settings',
      'go-live',
      'tax-groups',
      'platforms',
      'platform-availability',
      'stacks',
      'field-settings',
    ],
  },
];

/**
 * Re-stack already-permission-filtered sections into {@link NAV_DISPLAY_GROUPS}.
 *
 * Takes the sections the caller has ALREADY filtered, so this function has no
 * opinion about visibility and cannot widen it: an item absent from the input
 * is absent from the output. A group whose items are all hidden yields no
 * group at all, so a heading never renders over an empty list.
 */
export function groupNavForDisplay(sections: readonly NavSection[]): NavSection[] {
  const byId = new Map<string, { item: NavItem; sectionTitle: string }>();
  for (const section of sections) {
    for (const item of section.items) {
      byId.set(item.id, { item, sectionTitle: section.title });
    }
  }

  const out: NavSection[] = [];
  const placed = new Set<string>();
  for (const group of NAV_DISPLAY_GROUPS) {
    const items: NavItem[] = [];
    for (const id of group.itemIds) {
      const found = byId.get(id);
      if (found === undefined) continue;
      items.push(found.item);
      placed.add(id);
    }
    if (items.length > 0) {
      out.push({ title: group.title ?? '', items });
    }
  }

  // FAIL OPEN — see the header. Anything this constant forgot keeps its own
  // registry heading rather than disappearing.
  const leftovers = new Map<string, NavItem[]>();
  for (const [id, { item, sectionTitle }] of byId) {
    if (placed.has(id)) continue;
    const bucket = leftovers.get(sectionTitle) ?? [];
    bucket.push(item);
    leftovers.set(sectionTitle, bucket);
  }
  for (const [title, items] of leftovers) out.push({ title, items });

  return out;
}

/** Every registry id this constant does not place — asserted EMPTY by the suite. */
export function ungroupedNavItemIds(sections: readonly NavSection[]): string[] {
  const placed = new Set(NAV_DISPLAY_GROUPS.flatMap((g) => g.itemIds));
  return sections.flatMap((s) => s.items.map((i) => i.id)).filter((id) => !placed.has(id));
}

/** Every id this constant names that no registry defines — asserted EMPTY too. */
export function unknownNavDisplayIds(sections: readonly NavSection[]): string[] {
  const known = new Set(sections.flatMap((s) => s.items.map((i) => i.id)));
  return NAV_DISPLAY_GROUPS.flatMap((g) => g.itemIds).filter((id) => !known.has(id));
}
